import { Injectable } from '@angular/core';
import { LoginDto } from '../../models/dto/loginDTO';
import { isNoEmptyArray, isNotEmptyString, isNullOrUndefined, isObject, isValidString } from '@pineapplelab/util';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { BrowserStorage } from '../../models/browserStorage';
import { User } from '../../models/user';
import { DbService } from '../db/db.service';
import { ApiService } from '../api/api.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Router } from '@angular/router';
import { RouteEnum } from '../../enums/routes.enum';
import { ForgotPasswordDto } from '../../models/dto/forgotPasswordDTO';
import { UpdatePasswordDto } from '../../models/dto/updatePasswordDTO';
import { ValidateTokenDto } from '../../models/dto/validateTokenDTO';
import { Auth, getAuth, onIdTokenChanged } from '@angular/fire/auth';
import { ICreateClient } from '../../models/dto/createClient';
import { jwtDecode } from 'jwt-decode';
import { Category } from '../../models/category';
import { DocumentReference } from '@angular/fire/compat/firestore';
import { environment } from '../../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import firebase from "firebase/compat/app";
import { ConfigService } from '../config/config.service';
import { UpdateForgotPasswordDto } from '../../models/dto/updateForgotPasswordDTO';
import { AnalyticsService } from '../analytics/analytics.service';
import * as Sentry from '@sentry/angular';
import { clearStoredSummaries } from '../my-courses-summary/my-courses-summary.mapper';

@Injectable({
	providedIn: 'root'
})
export class AuthService {

	auth: { user: User, permissions: number[] } = { user: null, permissions: [] }
	projectId = environment.projectId;
	private _urlAfterLogin: string = null;
	private _isLoggingOut: boolean = false;
	/** Id of the user whose Firebase session has been confirmed to match `auth.user`. */
	private verifiedUid: string = null;
	private tokenListenerAttached: boolean = false;

	isLoggedStatus = new BehaviorSubject<boolean>(false);

	constructor(
		private api: ApiService,
		private angularFireAuth: AngularFireAuth,
		private db: DbService,
		private router: Router,
		private dialogRef: MatDialog,
		private configService: ConfigService,
		private analyticsService: AnalyticsService
	) {}

	async login(user: LoginDto, showAPIError: boolean = false) {
		const result = await this.api.post(`/auth/login`, user.toJson(), { showAPIError })
			.then(async r => {
				if (!isNotEmptyString(r?.['accessToken']) && (!isObject(r) || !isObject(r.data) || !isNotEmptyString(r.data.accessToken))) {
					return null;
				}
				const responseUser = await this.afterLoginActions(r?.['accessToken'] || r.data.accessToken);
				return responseUser;
			})
		if ((isNullOrUndefined(result))) {
			return result;
		}
		return result;
	}

	async afterLoginActions(customToken: string): Promise<any> {
		if (!customToken) {
			return;
		}
		// A custom token is only a one-time credential for signing in. It cannot be sent to APIs
		// guarded by Admin SDK verifyIdToken(), which accepts the exchanged Firebase ID token.
		const credential = await this.angularFireAuth.signInWithCustomToken(customToken);
		const idToken = await credential.user.getIdToken();

		const { permissions, user } = this.getUserData(idToken);
		this.auth.user = new User(user);
		this.auth.permissions = permissions;
		this.verifiedUid = credential.user.uid;
		this.setSentryUser(this.auth.user);
		// Store only ID tokens. The interceptor reads this value for every authenticated API call.
		BrowserStorage.set('userToken', idToken);
		this.listenTokenChangesOnce();
		this.isLoggedStatus.next(true);
		this.analyticsService.log('login', { method: 'email', userId: this.auth.user?.id, client: this.auth?.user?.client });
		return user;
	}

	isUserLogged(){
		return this.auth?.user !== null;
	}

	private getUserData(token: string) {
		try {
			const data = jwtDecode(token) as any;
			const claimsUser = data?.claims?.user ?? data?.user;
			const claimsPermissions = data?.claims?.permissions ?? data?.permissions;

			const user = isObject(claimsUser)
				? {
					...claimsUser,
					assigned_categories: this.getDocumentReferenceFromTokenData(claimsUser.assigned_categories, this.projectId),
					created_at: this.getTimestampFromTokenData(claimsUser?.created_at),
					updated_at: this.getTimestampFromTokenData(claimsUser?.updated_at),
					expiration_date: this.getTimestampFromTokenData(claimsUser?.expiration_date),
				}
				: null;

			const { permissions: projectPermissions } = this.configService.getConfig();
			let permissions = Array.isArray(claimsPermissions) ? claimsPermissions : [];
			permissions = permissions.concat(projectPermissions);

			return { user, permissions };
		} catch (error) {
			return { user: null, permissions: [] };
		}
	}

	async forgotPassword(dto: ForgotPasswordDto) {
		const result = await this.api.post(`/auth/forgot-password`, dto.toJson(), { showAPIError: false });
		return result;
	}

	async updatePassword(dto: UpdateForgotPasswordDto) {
		const result = await this.api.post(`/auth/update-password`, dto.toJson(), { showAPIError: true });
		return result;
	}

	async syncPasswordFromLink(username: string, newPassword: string): Promise<void> {
		const res = await this.api.get(`/auth/user-id-by-username?username=${encodeURIComponent(username)}`, { showAPIError: false }) as { userId?: string; data?: { userId: string } };
		const userId = res?.userId ?? res?.data?.userId;
		if (!userId) {
			throw new Error('invalid_user');
		}
		await this.api.post(`/auth/reset-password`, { userId, newPassword }, { showAPIError: false });
	}

	/** Creates a client account right away with the password the admin set. Requires CREATE_CLIENT. */
	async createClient(client: ICreateClient): Promise<void> {
		await this.api.post(`/auth/client`, client, { showAPIError: false });
	}

	/** Sets a client's password and signs them out of their sessions. Requires EDIT_CLIENT. */
	async resetClientPassword(userId: string, newPassword: string): Promise<void> {
		await this.api.post(`/auth/client-password`, { userId, newPassword }, { showAPIError: false });
	}

	async updatePasswordLogged(dto: UpdatePasswordDto) {
		const result = await this.api.post(`/auth/update-password-logged`, dto.toJson(), { showAPIError: false });
		return result;
	}

	async validateToken(dto: ValidateTokenDto) {
		const result = await this.api.post(`/auth/valid-token`, dto.toJson());
		return result;
	}

	private async listenTokenChanges(auth: Auth) {
		onIdTokenChanged(auth, async (user) => {
			if (!user) {
				this.clearAuthState();
				if (!this._isLoggingOut) {
					await this.router.navigateByUrl(`/${RouteEnum.LOGIN}`);
				}
				return;
			}

			const token = (await user.getIdTokenResult()).token;
			BrowserStorage.set('userToken', token);
			const { permissions, user: tokenUser } = this.getUserData(token);
			if (!isNullOrUndefined(tokenUser)) {
				this.auth.user = new User(tokenUser);
				this.auth.permissions = permissions;
				// This token came from the live Firebase session, so it defines the identity every
				// write must use from here on — including after a switch to a different account.
				this.verifiedUid = user.uid;
				this.isLoggedStatus.next(true);
			}
		});
	}

	async isAuth() {
		const firebaseUser = await firstValueFrom(this.angularFireAuth.user)
		if (isNullOrUndefined(firebaseUser)) {
			this.clearAuthState();
			return false;
		}
		const storedToken = BrowserStorage.get('userToken');
		const { user: storedUser } = this.getUserData(storedToken);

		if (!(await this.restoreSession(firebaseUser, storedUser))) {
			return false;
		}

		// Existing sessions may still have the custom token written by an older release. Exchange it
		// for the current Firebase ID token before the route can issue any authenticated API calls.
		const idToken = await firebaseUser.getIdToken();
		BrowserStorage.set('userToken', idToken);
		const { permissions, user } = this.getUserData(idToken);

		this.auth.user = new User(user);
		this.auth.permissions = permissions;
		this.verifiedUid = firebaseUser.uid;
		this.listenTokenChangesOnce();
		this.setSentryUser(isNullOrUndefined(user) ? null : this.auth.user);
		this.isLoggedStatus.next(!isNullOrUndefined(user));
		return !isNullOrUndefined(user);
	}

	/**
	 * Guards the one case where a restored session is unusable: the stored `userToken` describes a
	 * different account than the persisted Firebase session.
	 *
	 * The app carries two independent identities — `auth.user`, decoded from the token in
	 * localStorage, and the Firebase Auth session, which is what `request.auth.uid` resolves to in
	 * the security rules. A login mints them together (`createCustomToken(user.id, …)`), but they are
	 * stored separately, so on a shared device they can end up describing different people: one is
	 * replaced while the other survives.
	 *
	 * Nothing visible breaks when that happens. Reads are allowed to any signed-in user, so the app
	 * looks normal and the learner works through a whole course, while every write is rejected by
	 * `isOwner(userId)` and their progress is silently lost. Refusing the session here costs one
	 * re-login; allowing it costs the learner their training record.
	 */
	private async restoreSession(firebaseUser: firebase.User, tokenUser: { [key: string]: any } | null): Promise<boolean> {
		const tokenUserId = tokenUser?.['id'] ?? null;
		if (!tokenUserId || firebaseUser.uid === tokenUserId) {
			return true;
		}

		Sentry.captureMessage('Auth identity mismatch on session restore', {
			level: 'error',
			extra: { firebaseUid: firebaseUser.uid, tokenUserId },
		});
		await this.logout();
		return false;
	}

	/** `onIdTokenChanged` is registered once per app instance; re-registering would stack listeners. */
	private listenTokenChangesOnce(): void {
		if (this.tokenListenerAttached) {
			return;
		}
		this.tokenListenerAttached = true;
		this.listenTokenChanges(getAuth());
	}

	/**
	 * Attaches the signed-in user to every Sentry event (errors, console captures,
	 * slow-load reports) so a report from the field can be tied to an account.
	 * Called on fresh login, on session restore, and with null on logout.
	 */
	private setSentryUser(user: User | null): void {
		if (!user?.id) {
			Sentry.setUser(null);
			Sentry.setTag('client', undefined);
			Sentry.setTag('role', undefined);
			return;
		}
		Sentry.setUser({ id: user.id, username: user.username });
		Sentry.setTag('client', user.client ?? undefined);
		Sentry.setTag('role', user.role != null ? String(user.role) : undefined);
	}

	async logout(redirectTo: string = `/${RouteEnum.LOGIN}`) {
		this._isLoggingOut = true;
		this.clearAuthState();
		clearStoredSummaries();
		if (this.dialogRef?.openDialogs && isNoEmptyArray(this.dialogRef.openDialogs)) {
			this.dialogRef.closeAll();
		}
		return await this.angularFireAuth.signOut().finally(() => {
			this.analyticsService.log('logout');
			(!!redirectTo ? this.router.navigateByUrl(redirectTo) : Promise.resolve()).then(() => this._isLoggingOut = false);
			return true;
		})
	}

	private clearAuthState(): void {
		this.auth.user = null;
		this.auth.permissions = [];
		this.verifiedUid = null;
		this.setSentryUser(null);
		BrowserStorage.delete('userToken');
		BrowserStorage.delete('user');
		this.isLoggedStatus.next(false);
	}

	async updateActiveAndExpirationDate(user: User, projectId: string) {
		const snapshot = await this.db.getProyIdRef(projectId).collection(User.PATH).doc(user.id)
			.update({
				['status']: user.status,
				['expiration_date']: user.expirationDate
			});
	}

	hasPermission(permission: number | number[], inclusive = true) {
		let access = false;
		if (!isNoEmptyArray(this.auth.permissions)) {
			return false;
		}
		if (isNullOrUndefined(permission)) {
			return false;
		}
		if (!Array.isArray(permission)) {
			access = this.auth.permissions.includes(permission);
			return access;
		}
		if (inclusive) {
			access = permission.every((val, i) => this.auth.permissions.includes(val));
		} else {
			access = permission.some(p => this.auth.permissions.includes(p));
		}
		return access;
	}

	private getDocumentReferenceFromTokenData(data: any[], projectId: string): DocumentReference[] {
		if (!data || !isNoEmptyArray(data)) {
			return []
		}

		const refBase = this.db.getProyIdRef(projectId).collection(Category.PATH);
		return data.map(category => refBase.doc(category?._path?.segments[category?._path?.segments?.length - 1])) as DocumentReference[];
	}

	setUrlAfterLogin(url: string): void {
		if (isValidString(url) && !url.includes('login')) {
			this._urlAfterLogin = url;
		}
	}

	getUrlAfterLogin(): string {
		const result = this._urlAfterLogin;
		if (isValidString(this._urlAfterLogin)) {
			this._urlAfterLogin = null;
		}
		return result;
	}

	getTimestampFromTokenData(data: any): firebase.firestore.Timestamp {
		if (!data) {
			return null;
		}
		const seconds = data?._seconds ?? data?.Seconds ?? data?.seconds;
		return firebase.firestore.Timestamp.fromDate(new Date(seconds * 1000));
	}

	getIsLoggingOut(): boolean {
		return this._isLoggingOut;
	}

	async getAccessTokenFromSSO(ssoToken: string): Promise<string> {
		if (!ssoToken || !isValidString(ssoToken)) {
			return null;
		}

		const accessToken = await this.api.post(`/auth/sso-login?token=${ssoToken}`, {});
		return accessToken?.data ?? (accessToken as any)?.token ?? accessToken;
	}

}
